import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { WorkModel } from "../../../factories/works/models/work.model";
import { useListMemory, useRememberList } from "../../../hooks/useListMemory";
import { useWorksSearchParams } from "../../../hooks/useWorksSearchParams";
import { useAppDispatch, useAppSelector } from "../../../redux/redux";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import { useGetClientTeamClientsQuery } from "../../../slices/ClientTeamSlice";
import { useGetRolesQuery } from "../../../slices/RoleSlice";
import { useGetWorksQuery, workSlice } from "../../../slices/WorkSlice";
import {
  effectiveClientIds,
  resolvePeople,
  teamFilterActive,
} from "../../clientTeam/clientTeam.utils";
import { WorkTableSort } from "../types/work-table";
import { useWorkBulkActions } from "./WorkBulkActions";
import {
  BoardFilters,
  BoardMemory,
  boardMemoryOrPreset,
  EMPTY_BOARD_FILTERS,
  expandStatusFilter,
  filterQueuedWorks,
  matchesStatuses,
  myRoleIds,
  presetParamsFor,
  roleCounts,
  statDueRange,
  StatFilter,
  statusMenuItems,
  tabHasStats,
  tabQuery,
  WorksTab,
  workTypeCounts,
} from "./worksBoard.utils";

export type { BoardFilters } from "./worksBoard.utils";
export { EMPTY_BOARD_FILTERS } from "./worksBoard.utils";

export const PAGE_SIZE = 20;
type StatusOption = { label: string; color?: string; isBold?: boolean };
const NO_STATUS_OPTIONS: StatusOption[] = [];
/** Queued works are filtered by role in the browser, so load them all. */
const QUEUED_FETCH_SIZE = 500;
/** Status / work type filter counts are counted in the browser. */
const FILTER_COUNT_FETCH_SIZE = 2000;

// Card order on the Work page (Roadmap 1865).
const STATS: StatFilter[] = ["total", "dueWeek", "overdue", "later"];

const totalOf = (works?: WorkModel[]) => works?.[0]?.totalRecords ?? 0;

/** Base request for the works list: the tab, plus the user's filters. */
const useWorksArgs = (
  tab: WorksTab,
  filters: BoardFilters,
  /** Clients matched by the Client owner / manager filters (undefined = no such filter). */
  teamClientIds?: string[],
) => {
  const { user } = useAppSelector((state) => state.appReducer);
  const orgId = getLinkedFirm()?.orgId || "";

  return useMemo(() => {
    const base = tabQuery(tab, user.id);
    const userIds = tab === "my" ? base.userIds ?? [] : filters.userIds;
    const workStatuses = filters.statuses.length
      ? filters.statuses
      : (base as { workStatuses?: string[] }).workStatuses ?? [];
    return {
      orgId,
      currentUserId: user.id,
      customerIds: effectiveClientIds(
        filters.clientIds,
        teamClientIds !== undefined,
        teamClientIds,
      ),
      userIds,
      worktypeIds: filters.workTypeIds,
      workStatuses,
      isArchived: (base as { isArchived?: boolean }).isArchived,
      isQueued: (base as { isQueued?: boolean }).isQueued,
      hideGreenStatus: base.hideGreenStatus,
    };
  }, [tab, filters, orgId, user.id, teamClientIds]);
};

/** Count badges of the five tabs (no filters). */
export const useTabCounts = () => {
  const { user } = useAppSelector((state) => state.appReducer);
  const orgId = getLinkedFirm()?.orgId || "";
  const skip = !orgId || !user.id;

  const countArgs = (tab: WorksTab) => ({
    orgId,
    currentUserId: user.id,
    pageNumber: 1,
    pageSize: 1,
    sortBy: WorkTableSort.StartDate,
    sortOrder: "desc" as const,
    ...tabQuery(tab, user.id),
  });

  const active = useGetWorksQuery(countArgs("active"), { skip });
  const my = useGetWorksQuery(countArgs("my"), { skip });
  const queued = useGetWorksQuery(countArgs("queued"), { skip });
  const completed = useGetWorksQuery(countArgs("completed"), { skip });
  const archived = useGetWorksQuery(countArgs("archived"), { skip });

  return {
    active: active.data ? totalOf(active.data) : undefined,
    my: my.data ? totalOf(my.data) : undefined,
    queued: queued.data ? totalOf(queued.data) : undefined,
    completed: completed.data ? totalOf(completed.data) : undefined,
    archived: archived.data ? totalOf(archived.data) : undefined,
  } as Record<WorksTab, number | undefined>;
};

export const useWorksBoard = (
  tab: WorksTab,
  /** The firm's status list (Select status options), to expand groups. */
  statusOptions: StatusOption[] = NO_STATUS_OPTIONS,
) => {
  const [searchParams] = useSearchParams();
  const { user } = useAppSelector((state) => state.appReducer);
  const orgId = getLinkedFirm()?.orgId || "";
  const skip = !orgId || !user.id;
  const isQueued = tab === "queued";
  const hasStats = tabHasStats(tab);

  const { page, sortBy, sortOrder, setPage, setSort, updateParams } =
    useWorksSearchParams({
      defaultSortBy: WorkTableSort.StartDate,
      defaultSortOrder: "desc",
    });

  // Filters, card and "My queued" survive opening a work item and coming
  // back with Back (Roadmap 1851); page and sort live in the address.
  const memory = useListMemory<BoardMemory>({ scope: orgId });
  const [initial] = useState(() =>
    boardMemoryOrPreset(tab, memory.restored, {
      userId: searchParams.get("userId"),
      // The Home page's pipeline opens a tab filtered to one status group.
      status: searchParams.get("status"),
    }),
  );
  const [filters, setFiltersState] = useState<BoardFilters>(initial.filters);
  const [stat, setStatState] = useState<StatFilter>(initial.stat);
  const [queuedScope, setQueuedScopeState] = useState<"all" | "mine">(
    initial.queuedScope,
  );
  const [selected, setSelected] = useState<WorkModel[]>([]);

  // Client owner / Client manager filters (Mg-231): find-work has no such
  // filter, so the matching clients are asked for and sent as its client filter.
  const teamOwners = useMemo(
    () => resolvePeople(filters.ownerIds ?? [], user.id),
    [filters.ownerIds, user.id],
  );
  const teamManagers = useMemo(
    () => resolvePeople(filters.managerIds ?? [], user.id),
    [filters.managerIds, user.id],
  );
  const teamActive = teamFilterActive(teamOwners, teamManagers);
  const { currentData: teamClients } = useGetClientTeamClientsQuery(
    { orgId, ownerIds: teamOwners, managerIds: teamManagers },
    { skip: !orgId || !teamActive },
  );
  const teamClientIds = teamActive ? teamClients?.customerIds : undefined;
  // Wait for the matching clients before asking for work.
  const skipWorks = skip || (teamActive && !teamClients);

  // Keep ?status= / ?userId= in the address in step with the filters (QA batch 12).
  const preset = presetParamsFor(tab, filters);
  useEffect(() => {
    if (
      (searchParams.get("status") || null) !== preset.status ||
      (searchParams.get("userId") || null) !== preset.userId
    ) {
      updateParams({ status: preset.status, userId: preset.userId });
    }
  }, [preset.status, preset.userId, searchParams, updateParams]);

  const setFilters = useCallback(
    (patch: Partial<BoardFilters>) => {
      setFiltersState((current) => ({ ...current, ...patch }));
      setPage(1);
    },
    [setPage],
  );
  const clearFilters = useCallback(() => {
    setFiltersState(EMPTY_BOARD_FILTERS);
    setPage(1);
  }, [setPage]);
  const setStat = useCallback(
    (next: StatFilter) => {
      setStatState(next);
      setPage(1);
    },
    [setPage],
  );
  const setQueuedScope = useCallback(
    (next: "all" | "mine") => {
      setQueuedScopeState(next);
      setPage(1);
    },
    [setPage],
  );

  // Same request without the status and work type filters, for the counts
  // in those two menus (each counted with the other one applied, 1834).
  const countFilters = useMemo(
    () => ({ ...filters, statuses: [], workTypeIds: [] }),
    [filters],
  );
  const countArgs = useWorksArgs(
    tab,
    countFilters,
    teamActive ? teamClientIds ?? [] : undefined,
  );

  const dueArgs = (which: StatFilter) => {
    const range = statDueRange(which);
    return range
      ? { dueDates: ["BetweenDates"], ...range }
      : { dueDates: [] as string[], fromDate: null, toDate: null };
  };

  // Status / work type menu counts (every tab, 1833/1834): the tab, the
  // other filters and the picked card, counted in the browser.
  const { data: countWorks } = useGetWorksQuery(
    {
      ...countArgs,
      ...(hasStats ? dueArgs(stat) : {}),
      pageNumber: 1,
      pageSize: FILTER_COUNT_FETCH_SIZE,
      sortBy: WorkTableSort.StartDate,
      sortOrder: "desc" as const,
    },
    { skip: skipWorks },
  );

  // A picked main group also asks for its sub-statuses (QA batch 16).
  const requestFilters = useMemo(() => {
    if (!filters.statuses.length) return filters;
    const statuses = expandStatusFilter(
      filters.statuses,
      statusMenuItems(statusOptions, countWorks),
    );
    return statuses.length === filters.statuses.length
      ? filters
      : { ...filters, statuses };
  }, [filters, statusOptions, countWorks]);
  const baseArgs = useWorksArgs(
    tab,
    requestFilters,
    teamActive ? teamClientIds ?? [] : undefined,
  );

  const listArgs = useMemo(
    () => ({
      ...baseArgs,
      ...(hasStats ? dueArgs(stat) : {}),
      pageNumber: isQueued ? 1 : page,
      pageSize: isQueued ? QUEUED_FETCH_SIZE : PAGE_SIZE,
      sortBy,
      sortOrder,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [baseArgs, hasStats, stat, isQueued, page, sortBy, sortOrder],
  );

  const {
    data: fetched = [],
    isLoading,
    isFetching,
  } = useGetWorksQuery(listArgs, { skip: skipWorks });

  // Stat card numbers (with the current filters).
  const statArgs = (which: StatFilter) => ({
    ...baseArgs,
    ...dueArgs(which),
    pageNumber: 1,
    pageSize: 1,
    sortBy: WorkTableSort.StartDate,
    sortOrder: "desc" as const,
  });
  const statTotal = useGetWorksQuery(statArgs("total"), {
    skip: skipWorks || !hasStats,
  });
  const statOverdue = useGetWorksQuery(statArgs("overdue"), {
    skip: skipWorks || !hasStats,
  });
  const statWeek = useGetWorksQuery(statArgs("dueWeek"), {
    skip: skipWorks || !hasStats,
  });
  const statLater = useGetWorksQuery(statArgs("later"), {
    skip: skipWorks || !hasStats,
  });
  // Card numbers follow the list (QA batch 16, WORK-004): when the list's own
  // total for the picked card differs from the card (work added/changed
  // since the cards were loaded), the cards are asked again.
  const listTotal = fetched.length ? totalOf(fetched) : undefined;
  const pickedStat = {
    total: statTotal,
    overdue: statOverdue,
    dueWeek: statWeek,
    later: statLater,
  }[stat];
  const pickedCount = pickedStat.data ? totalOf(pickedStat.data) : undefined;
  const refreshCards = useRef<() => void>(() => undefined);
  refreshCards.current = () => {
    [statTotal, statOverdue, statWeek, statLater].forEach((query) => {
      if (!query.isUninitialized) query.refetch();
    });
  };
  useEffect(() => {
    if (
      !hasStats ||
      isQueued ||
      isFetching ||
      listTotal === undefined ||
      pickedCount === undefined ||
      pickedStat.isFetching
    )
      return;
    if (listTotal !== pickedCount) refreshCards.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listTotal, isFetching]);
  const statCounts: Record<StatFilter, number | undefined> = {
    total: statTotal.data ? totalOf(statTotal.data) : undefined,
    overdue: statOverdue.data ? totalOf(statOverdue.data) : undefined,
    dueWeek: statWeek.data ? totalOf(statWeek.data) : undefined,
    later: statLater.data ? totalOf(statLater.data) : undefined,
  };

  // Roles (Queued tab: role filter, "My queued", member names on hover).
  const { data: roles = [] } = useGetRolesQuery({ orgId }, { skip: !orgId });
  const mine = useMemo(() => myRoleIds(roles, user.id), [roles, user.id]);
  const roleMembers = useMemo(
    () =>
      new Map(
        roles.map((role) => {
          const names = (role.userRoles ?? [])
            .map((member) =>
              `${member.user?.userProfile?.firstName ?? ""} ${
                member.user?.userProfile?.lastName ?? ""
              }`.trim(),
            )
            .filter(Boolean);
          return [
            role.id.toLowerCase(),
            names.length
              ? `${role.roleName}: ${names.join(", ")}`
              : role.roleName,
          ];
        }),
      ),
    [roles],
  );

  const queuedAll = useMemo(
    () =>
      isQueued
        ? filterQueuedWorks(
            fetched,
            filters.roleIds,
            queuedScope === "mine" ? mine : null,
          )
        : [],
    [isQueued, fetched, filters.roleIds, queuedScope, mine],
  );

  // Role filter choices: the firm's roles plus deleted roles still on queued work.
  const roleChoices = useMemo(() => {
    const choices = new Map(
      roles.map((role) => [role.id.toLowerCase(), role.roleName]),
    );
    if (isQueued) {
      fetched.forEach((work) => {
        const id = (work.roleId || "").toLowerCase();
        if (id && !choices.has(id)) choices.set(id, work.roleName || "Role");
      });
    }
    return [...choices]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [roles, fetched, isQueued]);

  // Queued: the role filter and "My queued" are applied in the browser.
  const countBase = useMemo(
    () =>
      countWorks && isQueued
        ? filterQueuedWorks(
            countWorks,
            filters.roleIds,
            queuedScope === "mine" ? mine : null,
          )
        : countWorks,
    [countWorks, isQueued, filters.roleIds, queuedScope, mine],
  );
  const statusCountWorks = useMemo(
    () =>
      countBase?.filter(
        (work) =>
          !filters.workTypeIds.length ||
          filters.workTypeIds.some(
            (id) => id.toLowerCase() === (work.workTypeId || "").toLowerCase(),
          ),
      ),
    [countBase, filters.workTypeIds],
  );
  const workTypeCountMap = useMemo(
    () =>
      countBase
        ? workTypeCounts(
            countBase.filter((work) => matchesStatuses(work, filters.statuses)),
          )
        : undefined,
    [countBase, filters.statuses],
  );

  // Queued "Select Role" counts: like the other menus, counted with every
  // other filter applied (My queued, status, work type) but not the role pick.
  const roleCountMap = useMemo(() => {
    if (!countWorks || !isQueued) return undefined;
    const scoped = filterQueuedWorks(
      countWorks,
      [],
      queuedScope === "mine" ? mine : null,
    );
    return roleCounts(
      scoped.filter(
        (work) =>
          matchesStatuses(work, filters.statuses) &&
          (!filters.workTypeIds.length ||
            filters.workTypeIds.some(
              (id) =>
                id.toLowerCase() === (work.workTypeId || "").toLowerCase(),
            )),
      ),
    );
  }, [
    countWorks,
    isQueued,
    queuedScope,
    mine,
    filters.statuses,
    filters.workTypeIds,
  ]);

  // Assignee menu counts: the server decides who a work item belongs to
  // (extra assignees, picked-up roles, unassigned work), so each person is
  // counted by the server, when the menu is opened.
  const dispatch = useAppDispatch();
  const [assigneeCounts, setAssigneeCounts] = useState<
    Record<string, number | undefined>
  >({});
  const assigneeCountKey = JSON.stringify([baseArgs, stat, hasStats]);
  // People whose counts were asked for (menu opened once): recounted when
  // the filters change.
  const countedUsers = useRef<string[]>([]);
  const loadAssigneeCounts = useCallback(
    (userIds: string[]) => {
      if (skipWorks || isQueued || tab === "my") return;
      countedUsers.current = userIds;
      userIds.forEach((userId) => {
        const request = dispatch(
          workSlice.endpoints.getWorks.initiate({
            ...baseArgs,
            userIds: [userId],
            ...(hasStats ? dueArgs(stat) : {}),
            pageNumber: 1,
            pageSize: 1,
            sortBy: WorkTableSort.StartDate,
            sortOrder: "desc" as const,
          }),
        );
        request
          .unwrap()
          .then((result) =>
            setAssigneeCounts((current) => ({
              ...current,
              [userId]: totalOf(result),
            })),
          )
          .catch(() => undefined)
          .finally(() => request.unsubscribe());
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, skipWorks, isQueued, tab, baseArgs, hasStats, stat],
  );
  useEffect(() => {
    setAssigneeCounts({});
    if (countedUsers.current.length) loadAssigneeCounts(countedUsers.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assigneeCountKey]);

  const remembered = useMemo<BoardMemory>(
    () => ({ filters, stat, queuedScope }),
    [filters, stat, queuedScope],
  );
  useRememberList(memory, remembered, !isLoading && !skipWorks);

  const total = isQueued ? queuedAll.length : totalOf(fetched);
  const works = isQueued
    ? queuedAll.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
    : fetched;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  useEffect(() => {
    setSelected([]);
  }, [tab, page, sortBy, sortOrder, filters, stat, queuedScope]);

  const bulk = useWorkBulkActions(selected, setSelected);

  return {
    filters,
    setFilters,
    statusCountWorks,
    workTypeCountMap,
    roleCountMap,
    assigneeCounts,
    loadAssigneeCounts,
    clearFilters,
    stat,
    setStat,
    stats: STATS,
    statCounts,
    queuedScope,
    setQueuedScope,
    roleChoices,
    roleMembers,

    works,
    total,
    page,
    totalPages,
    setPage,
    sortBy,
    sortOrder,
    setSort,
    isLoading,
    isFetching,

    selected,
    setSelected,
    changeStatus: bulk.changeStatus,
    archive: bulk.archive,
    remove: bulk.remove,
    isBusy: bulk.isBusy,
  };
};
